import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

import VMRSselect from './VMRSselect';
import C3Select from './C3Select';
import Reason from './Reason';

import { ItemHeader } from './LineItem';

import { Row, Col } from 'antd';
import { ArrowUpOutlined, UpOutlined } from '@ant-design/icons';
import Description from './Description';
import Labor from './Labor';
import Parts from './Parts';
import Miscellaneous from './Miscellaneous';
import Issues from './Issues';
import IssuesSelector from './IssueSelector';
import MultiSelect from './MultiSelect';
import VMRSSysCode from './VMRSSysCode';
import VMRSAsyCode from './VMRSAsyCode';
import VMRSCompCode from './VMRSCompCode';
import C3container from './C3container';
import ButtonsContainer from './ButtonsContainer';
import LaborsContainer from './LaborsContainer';
import PartsContainer from './PartsContainer';
import MiscContainer from './MiscContainer';
import IssuesContainer from './IssuesContainer';
import DefectsContainer from './DefectsContainer';
import PMsContainer from './PMsContainer';
import AINotifier from './AINotifier';
import InspectionDefects from './InspectionDefects';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(90deg);
  }
`;

const rotateReverse = keyframes`
  from {
    transform: rotate(90deg);
  }
  to {
    transform: rotate(0deg);
  }
`;

const slideDown = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 2000px;
    opacity: 1;
    overflow-y: scroll;
  }
`;

const slideUp = keyframes`
  from {
    max-height: 2000px;
    opacity: 1;
    overflow-y: scroll;
  }
  to {
    max-height: 0;
    opacity: 0;
  }
`;

const AccordionWrapper = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  background-color: white;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const AccordionIcon = styled.span`
  margin-right: 10px;
  transition: transform 0.3s ease;
  transform: rotate(${(props) => (props.isActive ? '90deg' : '0deg')});
  animation: ${(props) => (props.isActive ? rotate : rotateReverse)} 0.3s ease;
  display: inline-block;
`;

const AccordionContent = styled.div`
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  animation: ${(props) => (props.isActive ? slideDown : slideUp)} 0.3s ease
    forwards;
`;

const StyledRow = styled(Row)`
  padding-bottom: 10px;
  padding-left: 41px;
  padding-right: 18px;
`;

const Accordion = ({ children, toggleActive }) => {
  return (
    <div>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          onToggle: toggleActive,
        })
      )}
    </div>
  );
};

const AccordionItem = ({ header, children, isActive, onToggle, itemKey }) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current && isActive) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isActive]);

  return (
    <AccordionWrapper>
      <AccordionHeader>
        <UpOutlined
          rotate={isActive ? 0 : 90}
          style={{ color: '#123FBF' }}
          onClick={onToggle}
        />
        {header}
      </AccordionHeader>
      <AccordionContent isActive={isActive} ref={contentRef}>
        {isActive && children}
      </AccordionContent>
    </AccordionWrapper>
  );
};

export { Accordion, AccordionItem };

const allPossibleIssues = [
  { id: 1, priority: 'Critical', name: 'Engine - oil leaks, belts' },
  { id: 2, priority: 'Major', name: 'Crank shaft - Cracked' },
  { id: 3, priority: 'Minor', name: 'Tires worn out' },
  { id: 4, priority: 'Major', name: 'Brake pads - squeaking' },
];

function LineItemAccordion({
  moveItem,
  updateItens,
  updateTotalItems,
  deleteLineItem,
  item,
  updateVMRSCode,
  toggle,
  addSubItem,
  deleteSubItem,
  changeSubItem,
  changeItemProperty,
  isDragged,
  removeInspectionIssue,
  inspectionFormId,
  inspectionFormTitle,
}) {
  const [sys, asy, comp] = item?.vmrsCode
    ? item.vmrsCode.slice(0, 12).split('-')
    : ['', '', ''];

  return (
    <>
      <StyledRow>
        <Col span={12}></Col>
        <Col span={12}>
          <Row style={{ width: '90%' }}>
            <Col span={6}>Labor Cost:</Col>
            <Col span={6}>Parts Cost:</Col>
            <Col span={6}>Misc. Cost:</Col>
            <Col span={6}>Subtotal:</Col>
          </Row>
        </Col>
      </StyledRow>

      <Accordion toggleActive={() => toggle(item.key)}>
        <AccordionItem
          itemKey={item.key}
          isActive={item.active}
          header={
            <ItemHeader
              item={item}
              changeItemProperty={changeItemProperty}
              onSelectVMRSCode={(value) => updateVMRSCode(value, item.key)}
              deleteLineItem={() => {
                deleteLineItem(item.key);
              }}
            />
          }
        >
          <Row style={{ marginLeft: '10px', marginRight: '10px' }} gutter={16}>
            <Col span={24}>
              <AINotifier isAIgenerated={item.vmrs_full_code_ai} />
            </Col>
          </Row>
          <Row style={{ margin: '10px' }} gutter={16}>
            <Col span={8}>
              <VMRSSysCode
                itemKey={item.key}
                title="VMRS System Code:"
                vmrs_sys_code={item.vmrs_sys_code}
                changeItemProperty={changeItemProperty}
              />
            </Col>
            <Col span={8}>
              <VMRSAsyCode
                itemKey={item.key}
                title="VMRS Assembly Code:"
                vmrs_sys_code={item.vmrs_sys_code}
                vmrs_asm_code={item.vmrs_asm_code}
                changeItemProperty={changeItemProperty}
              />
            </Col>
            <Col span={8}>
              <VMRSCompCode
                itemKey={item.key}
                title="VMRS Action Code:"
                vmrs_sys_code={item.vmrs_sys_code}
                vmrs_asm_code={item.vmrs_asm_code}
                vmrs_comp_code={item.vmrs_comp_code}
                changeItemProperty={changeItemProperty}
              />
            </Col>
          </Row>
          <C3container
            itemKey={item.key}
            diagnosis={item.diagnosis}
            deleteSubItem={deleteSubItem}
            changeItemProperty={changeItemProperty}
          />
          <Row style={{ margin: '10px' }} gutter={16}>
            <Col span={24}>
              <Reason
                itemKey={item.key}
                reason={item.reason}
                changeItemProperty={changeItemProperty}
              />
            </Col>
          </Row>
          <Row style={{ margin: '10px' }} gutter={16}>
            <Col span={24}>
              <Description
                itemKey={item.key}
                description={item.description}
                changeItemProperty={changeItemProperty}
              />
            </Col>
          </Row>
          <ButtonsContainer
            addSubItem={(type, values) => addSubItem(item.key, type, values)}
          />
          <LaborsContainer
            itemKey={item.key}
            labours={item.labours}
            deleteSubItem={deleteSubItem}
            changeSubItem={changeSubItem}
            moveItem={moveItem}
            addSubItem={(type, values) => addSubItem(item.key, type, values)}
          />

          <PartsContainer
            itemKey={item.key}
            parts={item.parts}
            deleteSubItem={deleteSubItem}
            changeSubItem={changeSubItem}
            moveItem={moveItem}
            addSubItem={(type, values) => addSubItem(item.key, type, values)}
          />

          <MiscContainer
            itemKey={item.key}
            miscs={item.miscellaneous}
            deleteSubItem={deleteSubItem}
            changeSubItem={changeSubItem}
            moveItem={moveItem}
            addSubItem={(type, values) => addSubItem(item.key, type, values)}
          />
          {item.showServices && (
            <IssuesContainer
              itemKey={item.key}
              item={item}
              services={item.services}
              deleteSubItem={deleteSubItem}
              changeSubItem={changeSubItem}
              changeItemProperty={changeItemProperty}
            />
          )}
          {item.showDefects && (
            <DefectsContainer
              itemKey={item.key}
              defects={item.defects || []}
              deleteSubItem={deleteSubItem}
              changeSubItem={changeSubItem}
              changeItemProperty={changeItemProperty}
            />
          )}
          {item.showPms && (
            <PMsContainer
              itemKey={item.key}
              pms={item.pms || []}
              deleteSubItem={deleteSubItem}
              changeSubItem={changeSubItem}
              changeItemProperty={changeItemProperty}
            />
          )}
          {item.inspection_issues && (
            <InspectionDefects
              itemKey={item.key}
              data={item.inspection_issues || []}
              onDelete={removeInspectionIssue}
              inspectionFormId={inspectionFormId}
              inspectionFormTitle={inspectionFormTitle}
            />
          )}
        </AccordionItem>
      </Accordion>
    </>
  );
}

export default LineItemAccordion;
